import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Briefcase,
  FolderKanban,
  BookOpen,
  Brain,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Eye,
  Folder,
  BarChart3,
  Shield,
  Trash2,
  FileText,
  CreditCard,
  Inbox,
  Target,
  Gauge,
  Box,
  LogOut,
} from 'lucide-react'
import { KaryvaLogoAnimated3D } from './KaryvaLogoAnimated3D'
import { useAuth } from '../contexts/AuthContext'
import { useLabels } from '../contexts/LabelsContext'
import { useTranslation } from '../i18n/I18nContext'
import { ThemeToggle } from './ThemeToggle'

interface NavigationProps {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
}

export function Navigation({ isCollapsed, setIsCollapsed }: NavigationProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout: handleLogout } = useAuth()
  const { label } = useLabels()
  const { t } = useTranslation()
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])

  const toggleMenu = (menuId: string) => {
    if (expandedMenus.includes(menuId)) {
      setExpandedMenus(expandedMenus.filter(id => id !== menuId))
    } else {
      setExpandedMenus([...expandedMenus, menuId])
    }
  }

  const navItems = [
    { id: 'dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, path: '/dashboard' },
    { id: 'strategy', label: 'Strategy', icon: Target, path: '/strategy' },
    { id: 'product', label: 'Products', icon: Box, path: '/product' },
    { id: 'capacity', label: 'Capacity', icon: Gauge, path: '/capacity' },
    {
      id: 'portfolio',
      label: label('portfolio'),
      icon: Briefcase,
      expandable: true,
      subItems: [
        { id: 'portfolio-dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/portfolio/dashboard' },
        { id: 'portfolio-view', label: `See existing ${label('portfolio', 'plural').toLowerCase()}`, icon: Eye, path: '/portfolio' },
        { id: 'portfolio-add', label: `Create new ${label('portfolio').toLowerCase()}`, icon: Plus, path: '/portfolio/create' },
        { id: 'portfolio-analytics', label: `${label('portfolio')} Analytics`, icon: BarChart3, path: '/portfolio/analytics' },
        { id: 'intake', label: 'Project requests', icon: Inbox, path: '/intake' },
      ],
    },
    {
      id: 'programs',
      label: label('program', 'plural'),
      icon: FolderKanban,
      expandable: true,
      subItems: [
        { id: 'program-dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/program/dashboard' },
        { id: 'program-view', label: `See existing ${label('program', 'plural').toLowerCase()}`, icon: Eye, path: '/program' },
        { id: 'program-add', label: `Create a new ${label('program')}`, icon: Plus, path: '/program/create' },
        { id: 'program-analytics', label: `${label('program')} Analytics`, icon: BarChart3, path: '/program/analytics' },
      ],
    },
    {
      id: 'project',
      label: label('project', 'plural'),
      icon: Folder,
      expandable: true,
      subItems: [
        { id: 'project-dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/project/dashboard' },
        { id: 'project-view', label: `See existing ${label('project', 'plural').toLowerCase()}`, icon: Eye, path: '/project' },
        { id: 'project-add', label: `Create new ${label('project').toLowerCase()}`, icon: Plus, path: '/project/create' },
        { id: 'project-workspace', label: `${label('project')} Workspace`, icon: Folder, path: '/project/workspace' },
      ],
    },
    { id: 'governance', label: t('nav.governance'), icon: Shield, path: '/governance' },
    { id: 'lessons', label: t('nav.lessons'), icon: BookOpen, path: '/lessons' },
    { id: 'ai', label: t('nav.ai'), icon: Brain, path: '/ai' },
    { id: 'templates', label: t('nav.templates'), icon: FileText, path: '/templates' },
    { id: 'recycle-bin', label: t('nav.recycleBin'), icon: Trash2, path: '/recycle-bin' },
    { id: 'billing', label: 'Billing & Plan', icon: CreditCard, path: '/billing' },
    { id: 'settings', label: t('nav.settings'), icon: Settings, path: '/settings' },
  ]

  // Auto-expand parent menus when navigating to a child route
  useEffect(() => {
    const toExpand: string[] = []
    navItems.forEach(item => {
      if (item.expandable && item.subItems) {
        const hasActiveChild = item.subItems.some(sub => sub.path && (
          location.pathname === sub.path ||
          location.pathname.startsWith(sub.path + '/')
        ))
        if (hasActiveChild) toExpand.push(item.id)
      }
    })
    if (toExpand.length > 0) {
      setExpandedMenus(prev => [...new Set([...prev, ...toExpand])])
    }
  }, [location.pathname])

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  const isActive = (path: string) => {
    // Exact match
    if (location.pathname === path) {
      return true
    }
    
    // List routes that should only match exactly, not as prefixes
    const exactMatchRoutes = [
      '/portfolio', '/portfolio/create', '/portfolio/dashboard', '/portfolio/analytics',
      '/governance',
      '/program', '/program/create', '/program/dashboard', '/program/analytics',
      '/project', '/project/create', '/project/dashboard', '/project/workspace',
      '/dashboard',
    ]
    if (exactMatchRoutes.includes(path)) {
      return false
    }
    
    // For other routes, allow matching child routes
    // This allows /portfolio/dashboard to match /portfolio/dashboard/...
    if (location.pathname.startsWith(path + '/')) {
      return true
    }
    
    return false
  }

  const handleLogoutClick = () => {
    handleLogout()
    navigate('/login')
  }

  return (
    <nav
      aria-label="Primary"
      className={`fixed left-0 top-0 h-screen bg-[#1a2942] text-white transition-all duration-300 z-50 flex flex-col ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Toggle tab — protrudes from the right edge of the sidebar */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        title={isCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        aria-label={isCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        aria-expanded={!isCollapsed}
        className="absolute -right-3 top-6 w-6 h-6 bg-[#DAA520] rounded-full flex items-center justify-center shadow-md hover:bg-[#c49518] transition-colors z-10"
      >
        {isCollapsed ? (
          <ChevronRight className="w-3.5 h-3.5 text-white" aria-hidden="true" />
        ) : (
          <ChevronLeft className="w-3.5 h-3.5 text-white" aria-hidden="true" />
        )}
      </button>

      {/* Header with Karyva Logo */}
      <div className="p-6 border-b border-[#3D4F61] flex flex-col gap-3">
        {isCollapsed ? (
          <div className="flex justify-center">
            <KaryvaLogoAnimated3D size="compact" darkBg={true} />
          </div>
        ) : (
          <>
            <KaryvaLogoAnimated3D size="header" darkBg={true} />
            <div className="text-[#DAA520] text-xs tracking-wide font-medium">
              PMI-Aligned Portfolio Management
            </div>
          </>
        )}
      </div>

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto p-2">
        {navItems.map((item) => {
          const Icon = item.icon
          const isExpanded = expandedMenus.includes(item.id)
          const isItemActive = item.path
            ? isActive(item.path)
            : (item.subItems?.some(sub => sub.path && (
                location.pathname === sub.path ||
                location.pathname.startsWith(sub.path + '/')
              )) ?? false)

          return (
            <div key={item.id}>
              {/* Main Menu Item */}
              <button
                onClick={() => {
                  if (item.expandable) {
                    if (isCollapsed) {
                      const first = item.subItems?.[0]?.path
                      if (first) handleNavigation(first)
                    } else {
                      toggleMenu(item.id)
                    }
                  } else if (item.path) {
                    handleNavigation(item.path)
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  isCollapsed ? 'justify-center' : ''
                } ${
                  isItemActive
                    ? 'bg-[#DAA520] text-white'
                    : 'text-[#C5CDD6] hover:bg-[#3D4F61] hover:text-white'
                }`}
                title={isCollapsed ? item.label : ''}
                aria-label={isCollapsed ? item.label : undefined}
                aria-current={isItemActive && item.path ? 'page' : undefined}
                aria-expanded={item.expandable ? isExpanded : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                {!isCollapsed && (
                  <>
                    <span className="text-sm flex-1 text-left">{item.label}</span>
                    {item.expandable && (
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    )}
                  </>
                )}
              </button>

              {/* Sub-menu Items */}
              {item.expandable && !isCollapsed && isExpanded && item.subItems && (
                <div className="ml-3 border-l-2 border-[#3D4F61] pl-2 mb-2">
                  {item.subItems.map((subItem) => {
                    const SubIcon = subItem.icon
                    const isSubActive = subItem.path ? isActive(subItem.path) : false
                    return (
                      <button
                        key={subItem.id}
                        onClick={() => subItem.path && handleNavigation(subItem.path)}
                        className={`w-full flex items-center gap-2 py-2 px-3 rounded-lg mb-1 text-sm transition-colors ${
                          isSubActive
                            ? 'bg-[#DAA520] text-white'
                            : 'text-[#A3B1BF] hover:bg-[#3D4F61] hover:text-white'
                        }`}
                        aria-current={isSubActive ? 'page' : undefined}
                      >
                        <SubIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                        <span>{subItem.label}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Theme Toggle */}
      {!isCollapsed && (
        <div className="p-4 border-t border-[#3D4F61]">
          <ThemeToggle />
        </div>
      )}

      {/* User Profile at Bottom */}
      <div className="p-4 border-t border-[#3D4F61] mt-auto">
        <div className={`flex items-center gap-3 ${isCollapsed ? 'justify-center' : ''}`}>
          <div className="w-10 h-10 bg-[#DAA520] rounded-full flex items-center justify-center text-white font-medium">
            {user?.full_name
              ? user.full_name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)
              : user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm truncate">{user?.full_name || user?.email || 'User'}</div>
              {user?.full_name && (
                <div className="text-[#A3B1BF] text-xs truncate">{user?.email || ''}</div>
              )}
            </div>
          )}
        </div>
        {!isCollapsed && (
          <button
            onClick={handleLogoutClick}
            className="w-full mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-[#A3B1BF] hover:bg-[#3D4F61] hover:text-white transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            <span>{t('nav.logout')}</span>
          </button>
        )}
      </div>
    </nav>
  )
}

